import { AuthEventTypeV4 } from '../../common/analytics';
import { CommonErrorType, DialogType } from '../../common/enums';
import {
  customerServiceNumberLabelMsg,
  customerServiceNumberOpenLongMsg,
  customerServiceNumberPriceMsg,
  notAbleToProcessContactRequestMsg,
  pleaseContactCustomerServiceMsg,
  somethingWentWrongMsg,
  t,
} from '../../common/i18n';
import { getCookie } from '../../common/utils/cookieUtils';
import { convertToCommonErrors, convertToCommonErrorsWithMessage } from '../../common/utils/errorUtils';
import { isInBrowser } from '../../common/utils/ssrUtils';
import { AuthenticatedUserRole } from '../../generated/api/authenticatedUserRole';
import { ConflictedAuthenticationResult } from '../../generated/api/conflictedAuthenticationResult';
import { ValidationConflict } from '../../generated/api/validationConflict';
import { getConflictingErrors } from '../../selfservice/epics/authenticatedUserEpic';
import { clearOnboardingId } from '../../selfservice/epics/epicUtils';
import { updateDialog } from './dialogStore.js';
import type { User } from './authState';
import type { AuthEventV4 } from '../../common/analytics';
import type { SwitchAccountJourneyPoint } from '../../common/enums';
import type { DialogParams } from '../../common/types/dialog';
import type { CommonError } from '../../common/types/errors';
import type { AuthenticationResult } from '../../generated/api/authenticationResult';
import type { ValidationConflictResponse } from '../../generated/api/validationConflictResponse';

export const reduceAuthenticationResult = (user: User, authenticationResult: AuthenticationResult) => {
  user.authenticated = {
    ...(user.authenticated || {}),
    analyticsConsent: authenticationResult.contact?.person?.analyticsConsent,
    businessId: authenticationResult.account?.businessId,
    chatToken: authenticationResult.token,
    companyName: authenticationResult.account?.accountName || '',
    contact: authenticationResult.contact,
    costCenter: authenticationResult.contact?.person?.costCenter,
    email: authenticationResult.contact?.person?.email,
    employeeNumber: authenticationResult.contact?.person?.employeeNumber,
    firstName: authenticationResult.contact?.person?.firstName || '',
    lastName: authenticationResult.contact?.person?.lastName,
    mdmId: authenticationResult.account?.accountMasterId,
    mobile: authenticationResult.contact?.person?.phoneNumber,
    preferredLanguage: authenticationResult.contact?.person?.preferredLanguage || 'fi',
    selfServiceInOec: authenticationResult.account?.selfServiceInOec,
    deviceStoreInOec: authenticationResult.account?.deviceStoreInOec,
    userRightsInSalesforce: authenticationResult.account?.userRightsInSalesforce,
    expiringEppSubscriptions: authenticationResult?.expiringEppSubscriptions,
    secondaryAccounts: authenticationResult.secondaryAccounts,
    enabledFeatureFlags: authenticationResult.enabledFeatureFlags,
    customerLevelPriceOrDiscount: authenticationResult.customerLevelPriceOrDiscount,
    segmentPricingGroup: authenticationResult.segmentPricingGroup,
    saving: false,
    isFetching: false,
    genesysChatToken: authenticationResult.genesysChatToken,
    oecServiceAgreements: authenticationResult.oecServiceAgreements,
    ...authenticationResult.user,
  };
};

/**
 * Returns the params of the dialog that should be opened for a conflicted authentication error, or `undefined`
 * when the errors do not describe a conflict. Callers open the dialog themselves, and can use the presence of
 * a return value as the "was this a conflict?" signal.
 */
export const handleConflictedError = (
  errors?: CommonError[],
  journeyPoint?: SwitchAccountJourneyPoint
): DialogParams | undefined => {
  const conflictingErrors = getConflictingErrors(errors);
  if (conflictingErrors) {
    clearOnboardingId();
    switch (conflictingErrors.type) {
      case ConflictedAuthenticationResult.TypeEnum.CONFLICTING_ACCOUNT:
        return {
          type: DialogType.SWITCH_ACCOUNT,
          header: `${t.NW12('Confirm pairing subscription and user details to new employer')}${
            conflictingErrors.conflictingAccountName ? ` (${conflictingErrors.conflictingAccountName}).` : `.`
          }`,
          body: t.KJAE(
            'After confirmation you lose access to subscription and usage details from the duration with your previous employer.'
          ),
          journeyPoint: journeyPoint,
        };
      case ConflictedAuthenticationResult.TypeEnum.DUPLICATE_CONTACT:
      case ConflictedAuthenticationResult.TypeEnum.POSSIBLE_CONTACT_DUPLICATE:
        return {
          type: DialogType.DUPLICATE_CONTACT,
          header: t.AYKR(pleaseContactCustomerServiceMsg),
          body: t.EGFA(notAbleToProcessContactRequestMsg),
        };
      case ConflictedAuthenticationResult.TypeEnum.USER_NOT_FOUND:
        return {
          type: DialogType.USER_RIGHTS_IN_SALESFORCE,
        };
      case ConflictedAuthenticationResult.TypeEnum.CONFLICTING_CONTACT:
        return {
          type: DialogType.CONFLICTING_CONTACT,
        };
      default:
        return undefined;
    }
  }
  return undefined;
};

/**
 * Opens the dialog for a failed authentication: either a specific follow-up dialog (`params`,
 * e.g. select username / select primary company / MFA), or a generic error message on the
 * currently visible dialog.
 */
export const showAuthFailedDialog = (params?: DialogParams, error?: string): void => {
  if (params) {
    updateDialog({ params, submitInProgress: false, visible: true });
  } else {
    updateDialog({
      errors: [{ type: CommonErrorType.SYSTEM, message: error || t.JDTO(somethingWentWrongMsg) }],
      submitInProgress: false,
    });
  }
};

export const handleAuthFailedError = (errors?: CommonError[], userName?: string, password?: string): void => {
  if (errors?.length) {
    const conflictError = errors[0].source?.error as unknown as ConflictedAuthenticationResult | undefined;
    if (conflictError?.type) {
      const error = conflictError;
      if (ConflictedAuthenticationResult.TypeEnum.MULTIPLE_USER_NAMES === error.type && error.userNames) {
        return showAuthFailedDialog({
          type: DialogType.SELECT_USERNAME,
          usernames: error.userNames,
        });
      } else if (
        ConflictedAuthenticationResult.TypeEnum.PRIMARY_ACCOUNT_NOT_SET === error.type &&
        error.availableAccounts &&
        password &&
        userName
      ) {
        return showAuthFailedDialog({
          availableAccounts: error.availableAccounts,
          password: password,
          type: DialogType.SELECT_PRIMARY_COMPANY,
          username: userName,
        });
      } else if (
        ConflictedAuthenticationResult.TypeEnum.PRIMARY_ACCOUNT_NOT_SET === error.type &&
        error.availableAccounts
      ) {
        return showAuthFailedDialog({
          availableAccounts: error.availableAccounts,
          type: DialogType.SELECT_PRIMARY_COMPANY,
        });
      } else if (ConflictedAuthenticationResult.TypeEnum.MOBILE_NUMBER_MISSING_MFA_REQUIRED === error.type) {
        return showAuthFailedDialog({
          type: DialogType.MFA_REQUIRED_PHONE,
        });
      } else if (ConflictedAuthenticationResult.TypeEnum.MOBILE_NUMBER_MISSING === error.type && password && userName) {
        return showAuthFailedDialog({
          password: password,
          type: DialogType.MISSING_MOBILE_NUMBER,
          username: userName,
        });
      }
    } else {
      return showAuthFailedDialog(
        undefined,
        t.JYLX(
          'Something went wrong, please try signing in again. If the problem persists, please call our customer service on {0} ({1}). Our customer service is open {2}.',
          customerServiceNumberLabelMsg,
          t.HQWW(customerServiceNumberPriceMsg),
          t.HQWV(customerServiceNumberOpenLongMsg)
        )
      );
    }
  }
  return showAuthFailedDialog();
};

const redirectToOecFromPaths = ['omaelisa', 'kassa', 'työntekijä'];

const userRoleToLoginStatus = (userRole: AuthenticatedUserRole) => {
  if (['KEY_USER', 'ADMIN', 'CUSTOMER_ADMIN', 'ELISA_ADMIN'].includes(userRole)) {
    return 'ADMIN';
  } else if (['EMPLOYEE', 'CUSTOMER_EMPLOYEE', 'PUNCHOUT_USER'].includes(userRole)) {
    return 'EMPLOYEE';
  }
  return undefined;
};

export const isCustomer = (role?: AuthenticatedUserRole) =>
  role &&
  [
    AuthenticatedUserRole.CUSTOMER_ADMIN,
    AuthenticatedUserRole.CUSTOMER_EMPLOYEE,
    AuthenticatedUserRole.KEY_USER,
    AuthenticatedUserRole.EMPLOYEE,
  ].includes(role);

export const isCustomerAdmin = (role?: AuthenticatedUserRole) =>
  role && [AuthenticatedUserRole.CUSTOMER_ADMIN, AuthenticatedUserRole.KEY_USER].includes(role);

export const handleRedirectResponse = (response: Response, forceRedirect = false) => {
  const redirectUrl = response.headers.get('Location');
  if (isInBrowser() && redirectUrl && window.location?.pathname) {
    // note, path always contains slash so this works
    const firstPath = window.location.pathname.split('/')[1];
    if (forceRedirect || redirectToOecFromPaths.some(redirectPath => firstPath.toLowerCase() === redirectPath)) {
      window.location.href = redirectUrl;
    }
  }
};

export const userAuthValidate = (authenticationResult: AuthenticationResult): AuthEventV4 => ({
  event: AuthEventTypeV4.USER_AUTH,
  login_status: userRoleToLoginStatus(authenticationResult.user.userRole), // eslint-disable-line @typescript-eslint/naming-convention
});

export const ssoNoeLoginSuccess = (authenticationResult: AuthenticationResult): AuthEventV4 => ({
  event: AuthEventTypeV4.LOGIN,
  login_service: 'ya_noe', // eslint-disable-line @typescript-eslint/naming-convention
  login_method: 'credentials', // eslint-disable-line @typescript-eslint/naming-convention
  login_status: userRoleToLoginStatus(authenticationResult.user.userRole), // eslint-disable-line @typescript-eslint/naming-convention
  sso_user_id: authenticationResult.user.userNameHash, // eslint-disable-line @typescript-eslint/naming-convention
});

const statusToFailureReason = (statusCode?: number): string => {
  switch (statusCode) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'user_not_found';
    case 409:
      return 'conflict';
    case 422:
      return 'unprocessable';
    default:
      return statusCode && statusCode >= 500 ? 'server_error' : 'unknown';
  }
};

const resolveFailureReason = (primary?: CommonError): string => {
  if (primary?.source?.error?.message?.includes('Invalid username/password')) {
    return 'invalid_credentials';
  }
  return statusToFailureReason(primary?.source?.statusCode);
};

const resolveAuthFailureFields = (errors: CommonError[]): Partial<AuthEventV4> => {
  const primary = errors[0];
  return {
    status_code: primary?.source?.statusCode, // eslint-disable-line @typescript-eslint/naming-convention
    error: primary?.source?.error?.errorType ?? primary?.type,
    error_category: primary?.category, // eslint-disable-line @typescript-eslint/naming-convention
    failure_reason: resolveFailureReason(primary), // eslint-disable-line @typescript-eslint/naming-convention
    error_description: errors.map(error => error.source?.error?.message ?? error.message).join(), // eslint-disable-line @typescript-eslint/naming-convention
  };
};

export const ssoNoeLoginFail = (errors: CommonError[]): AuthEventV4 => ({
  event: AuthEventTypeV4.LOGIN_FAILED,
  login_service: 'ya_noe', // eslint-disable-line @typescript-eslint/naming-convention
  login_method: 'credentials', // eslint-disable-line @typescript-eslint/naming-convention
  login_status: undefined, // eslint-disable-line @typescript-eslint/naming-convention
  ...resolveAuthFailureFields(errors),
});

export const elisaIdEoeLoginSuccess = (authenticationResult: AuthenticationResult): AuthEventV4 => ({
  event: AuthEventTypeV4.LOGIN,
  login_service: 'ya_eoe', // eslint-disable-line @typescript-eslint/naming-convention
  login_method: 'credentials', // eslint-disable-line @typescript-eslint/naming-convention
  login_status: userRoleToLoginStatus(authenticationResult.user.userRole), // eslint-disable-line @typescript-eslint/naming-convention
  amr_values: authenticationResult.amr, // eslint-disable-line @typescript-eslint/naming-convention
  eid_user_id: authenticationResult.trackingId, // eslint-disable-line @typescript-eslint/naming-convention
});

export const elisaIdEoeLoginFail = (errors: CommonError[]): AuthEventV4 => ({
  event: AuthEventTypeV4.LOGIN_FAILED,
  login_service: 'ya_eoe', // eslint-disable-line @typescript-eslint/naming-convention
  login_method: 'credentials', // eslint-disable-line @typescript-eslint/naming-convention
  login_status: undefined, // eslint-disable-line @typescript-eslint/naming-convention
  ...resolveAuthFailureFields(errors),
});

// filter errors which are not needed to be set in the state, because they are handled separately, e.g. showing a dialog on HTTP Status CONFLICT (409)
export const filterErrors = (errors?: CommonError[]) => {
  return errors?.filter(
    error =>
      error.source?.statusCode !== 409 ||
      (error.source?.statusCode === 409 &&
        (error.category === ConflictedAuthenticationResult.TypeEnum.ONBOARDING_LINK_USED_OR_EXPIRED ||
          error.category === ConflictedAuthenticationResult.TypeEnum.ONBOARDING_LINK_OLD_OR_INVALID))
  );
};

export const processConflictedErrors = (errors?: CommonError[]) => {
  return errors?.map(error => {
    if (
      error.source?.statusCode === 409 &&
      error.source?.error &&
      (error.source?.error as unknown as ConflictedAuthenticationResult)
    ) {
      const conflictingError = error.source.error as unknown as ConflictedAuthenticationResult;
      if (
        conflictingError?.type === ConflictedAuthenticationResult.TypeEnum.ONBOARDING_LINK_USED_OR_EXPIRED ||
        conflictingError?.type === ConflictedAuthenticationResult.TypeEnum.ONBOARDING_LINK_OLD_OR_INVALID
      ) {
        error.category = conflictingError.type;
      }
    }
    return error;
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toCommonErrors = async (error: any): Promise<CommonError[]> => {
  if (error instanceof Response) {
    let errorBody;
    try {
      errorBody = await error.json();
    } catch (e) {
      errorBody = undefined;
    }
    return convertToCommonErrorsWithMessage(`ajax error ${error.status}`, error.status, errorBody) ?? [];
  }
  return convertToCommonErrors(error.message);
};

const ELISA_ID_V3_COOKIE_NAME = 'elisaIdV3';

export const isElisaIdV3Enabled = () => {
  return (isInBrowser() && getCookie(ELISA_ID_V3_COOKIE_NAME)?.includes('true')) ?? false;
};

export const isConflicting409Error = (commonErrors: CommonError[]): boolean =>
  commonErrors.length === 1 && commonErrors[0]?.source?.statusCode === 409;

export const getSignatoryRightsConflictType = (commonErrors: CommonError[]): ValidationConflict | undefined => {
  const validationConflict = (commonErrors[0]?.source?.error as unknown as ValidationConflictResponse)
    ?.validationConflict;
  if (!isConflicting409Error(commonErrors) || !isElisaIdV3Enabled()) {
    return undefined;
  }
  if (
    validationConflict === ValidationConflict.CHOOSE_SIGNATORY_RIGHT ||
    validationConflict === ValidationConflict.CHOOSE_ADMIN_OR_SIGNATORY_RIGHT
  ) {
    return validationConflict;
  }
  return undefined;
};

export const isAdditionalInfoRequiredError = (commonErrors: CommonError[]) => {
  const validationConflictResponse = commonErrors[0]?.source?.error as unknown as ValidationConflictResponse;
  return (
    isConflicting409Error(commonErrors) &&
    isElisaIdV3Enabled() &&
    validationConflictResponse?.validationConflict === ValidationConflict.ADDITIONAL_INFO_REQUIRED
  );
};

export const isInvitationAcceptanceRequired = (commonErrors: CommonError[]) => {
  const validationConflictResponse = commonErrors[0]?.source?.error as unknown as ValidationConflictResponse;
  return (
    isConflicting409Error(commonErrors) &&
    isElisaIdV3Enabled() &&
    validationConflictResponse?.validationConflict === ValidationConflict.ADDITIONAL_INFO_REQUIRED_FOR_INVITATION
  );
};

export const isIndividualNotStronglyIdentifiedError = (commonErrors: CommonError[]) => {
  const validationConflictResponse = commonErrors[0]?.source?.error as unknown as ValidationConflictResponse;
  return (
    isConflicting409Error(commonErrors) &&
    validationConflictResponse?.validationConflict === ValidationConflict.INDIVIDUAL_NOT_STRONGLY_IDENTIFIED
  );
};

export const isSameOrigin = () => window.self.location.origin === window.top?.location.origin;
